import { backendTaxTypeFromFrontendTaxType } from 'api/v2/taxes';
import dayjs from 'dayjs';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCustomerCountry from 'hooks/shared/useCustomerCountry';
import useGetSelectedYearTaxes from 'hooks/Taxes/useGetSelectedYearTaxes';
import { useMemo } from 'react';
import type { Period } from 'types/global.types';
import type { TaxTypeEnum } from 'types/taxes.types';
import type { TaxesByYear } from 'types/taxes.types';
import type { IUser, UserCountry } from 'types/users.types';
import getPeriodSlug from 'utils/taxes/getPeriodSlug';

type UseTaxLink = {
  link: string | null;
  taxId: string | null;
};

const getSelectedYear = (user: IUser, taxPeriod: Period | undefined): number =>
  user?.firstTaxComputationYear &&
  taxPeriod?.year &&
  user.firstTaxComputationYear >= taxPeriod.year
    ? user.firstTaxComputationYear
    : taxPeriod?.year || dayjs().year();

const useTaxLink = (taxPeriod?: Period, taxType?: TaxTypeEnum): UseTaxLink => {
  const country = useCustomerCountry();
  const user = useGetCurrentCustomerData().data as IUser;

  const selectedYear = getSelectedYear(user, taxPeriod);

  const taxesOfSelectedYear = useGetSelectedYearTaxes(selectedYear)
    .data as TaxesByYear;

  const relevantTaxItem = useMemo(() => {
    if (!taxesOfSelectedYear?.data) return null;

    for (const tax of Object.values(taxesOfSelectedYear.data)) {
      const relevantTax = tax.items.find((item) => {
        const { type, period } = item;
        if (!type || !period) return false;

        // Check if all tax period properties match
        const isMatchingType = type === taxType;
        const isMatchingMonth = period.month === taxPeriod?.month;
        const isMatchingQuarter = period.quarter === taxPeriod?.quarter;
        const isMatchingYear = period.year === taxPeriod?.year;

        return (
          isMatchingType &&
          isMatchingMonth &&
          isMatchingQuarter &&
          isMatchingYear
        );
      });

      if (relevantTax) return relevantTax;
    }
  }, [taxesOfSelectedYear, taxPeriod, taxType]);

  if (!relevantTaxItem || !country || !taxPeriod)
    return { link: null, taxId: null };

  const slug =
    backendTaxTypeFromFrontendTaxType[country][
      taxType as keyof typeof backendTaxTypeFromFrontendTaxType[UserCountry]
    ];
  if (!slug) return { link: null, taxId: null };

  const periodSlug = getPeriodSlug({ period: taxPeriod });

  return {
    link: `/taxes/${country}/${slug}/${periodSlug}`,
    taxId: relevantTaxItem._id,
  };
};

export default useTaxLink;
